var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNotification } from '../notifications';
/**
 * Custom React hook to handle API calls using Axios.
 *
 * This hook simplifies the process of making API calls, handling different states such as loading, success, and error,
 * and managing the lifecycle of the request (including aborting pending requests). It also integrates with a notification
 * system to display success or error messages.
 *
 * @template TPayload The type of the payload that will be sent with the API call.
 * @template TResult The expected result type that the API call will return.
 *
 * @param {Function} apiCall The API function to be called. This function should accept an AbortSignal as its first argument,
 * followed by any additional arguments required for the API call.
 * @param {TResult | null} initialData The initial state of the data before the API call is made. Defaults to null.
 * @param {boolean} shouldFetch A flag to determine if the API call should be executed.
 * @param {Array} args Additional arguments to be passed to the API call function. Change in args wil re-create execute fuction.
 *
 * @returns {ApiHookResult<TPayload, TResult>} An object containing:
 * - `data`: The data returned from the API call, or null if the call hasn't completed or has failed.
 * - `error`: An error message string if the API call fails, otherwise null.
 * - `status`: A string representing the current status of the API call ('idle', 'loading', 'fetched', 'error').
 * - `execute`: A function to manually trigger the API call. Accepts an optional payload parameter.
 *
 * The hook handles aborting the API call when the component unmounts or a new API call is initiated.
 * It updates the component state based on the API call's progress and result, and triggers notifications
 * on success or failure.
 */
export var useApiCall = function (apiCall, initialData, shouldFetch, args, messages) {
    if (initialData === void 0) { initialData = null; }
    if (args === void 0) { args = []; }
    var liveNotification = useNotification().liveNotification;
    var _a = useState({
        data: initialData,
        error: null,
        status: 'idle'
    }), state = _a[0], setState = _a[1];
    var abortController = useRef(new AbortController());
    var isMounted = useRef(true);
    var resetTimeout = useRef(null);
    var execute = useCallback(function (payload) { return __awaiter(void 0, void 0, void 0, function () {
        var signal, params, response, err_1, errorMsg;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!shouldFetch)
                        return [2 /*return*/];
                    abortController.current.abort();
                    abortController.current = new AbortController();
                    signal = abortController.current.signal;
                    setState(function (prev) { return (__assign(__assign({}, prev), { status: 'loading' })); });
                    if (messages === null || messages === void 0 ? void 0 : messages.start) {
                        liveNotification('info', messages === null || messages === void 0 ? void 0 : messages.start);
                    }
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    if (!isMounted.current) {
                        return [2 /*return*/];
                    }
                    params = payload !== undefined ? __spreadArray(__spreadArray([], args, true), [payload], false) : __spreadArray([], args, true);
                    return [4 /*yield*/, apiCall.apply(void 0, __spreadArray([signal], params, false))];
                case 2:
                    response = _b.sent();
                    if (response.data || response.status === 200) {
                        setState({ data: response.data, error: null, status: 'fetched' });
                        if (messages === null || messages === void 0 ? void 0 : messages.success)
                            liveNotification('success', messages === null || messages === void 0 ? void 0 : messages.success);
                    }
                    else {
                        setState({ data: null, error: null, status: 'idle' });
                        liveNotification('success', 'Success');
                    }
                    return [3 /*break*/, 4];
                case 3:
                    err_1 = _b.sent();
                    if (!isMounted.current) {
                        return [2 /*return*/];
                    }
                    if (!signal.aborted) {
                        errorMsg = (messages === null || messages === void 0 ? void 0 : messages.error) || ((_a = err_1.response) === null || _a === void 0 ? void 0 : _a.data) || err_1.message;
                        setState({ data: null, error: errorMsg, status: 'error' });
                        liveNotification('error', errorMsg);
                    }
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, __spreadArray([shouldFetch], args, true));
    useEffect(function () {
        if (state.status === 'fetched') {
            // avoid overlapping calls
            if (resetTimeout.current) {
                clearTimeout(resetTimeout.current);
            }
            resetTimeout.current = setTimeout(function () {
                if (isMounted.current) {
                    setState(function (prev) { return (__assign(__assign({}, prev), { status: 'idle' })); });
                }
            }, 500);
        }
        return function () {
            if (resetTimeout.current) {
                clearTimeout(resetTimeout.current);
            }
        };
    }, [state.status]);
    // abort the request on unmount
    useEffect(function () {
        return function () {
            abortController.current.abort();
            isMounted.current = false;
            setState({ data: initialData, error: null, status: 'idle' });
        };
    }, []);
    return {
        data: state.data,
        error: state.error,
        status: state.status,
        execute: execute
    };
};
